import _ from "lodash";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import classes from "./MathEquationEditor.scss";
import {
  Button,
  EmptyState,
  IconButton,
  Tabs,
  Tooltip,
  TextAreaInput,
  DSProvider,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  CodeOutlined,
  DotsSixVerticalOutlined,
} from "@toddle-edu/ds-icons";
import { MathEquationMenu } from "./config";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { uploadFileV2 } from "modules/Services";
import { generateRandomId, getBasicDeviceInfo } from "Utils";

import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import MathLive from "./MathLive";
import { WarningIllustration } from "@toddle-edu/ds-theme";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import ACLStore from "lib/aclStore";
import { useDrag } from "Utils/CustomHooks";
import { convertMathToBlob } from "./mathFontManager";
import UpdateEquationsModal from "./UpdateEquationsModal";

const MATHFIELD_ID = "mathlive-field";
const SHADOW_ROOT_QUERY_SELECTOR = ".ML__content";
const MAX_EQUATION_WIDTH = 520;

// TODO: For dynamic matrix creation
// const generateMatrix = (rows = 3, cols = 3) => {
//   const matrixLatex = "\\begin{vmatrix} ";

//   for (let i = 0; i < rows; i++) {
//     for (let j = 0; i < cols; j++) {
//       matrixLatex.concat(
//         j === cols - 1 ? "\\placeholder{⬚} " : "\\placeholder{⬚} & "
//       );
//     }
//     if (i !== rows - 1) {
//       matrixLatex.concat(" \\\\");
//     }
//   }

//   matrixLatex.concat(" \\end{vmatrix}");
//   return matrixLatex;
// };

const getTabs = t => [
  { value: "BASIC", label: t("equationEditor:basic") },
  { value: "ADVANCE", label: t("equationEditor:advanced") },
  { value: "SETS_MATRICES", label: t("equationEditor:sets_matrices") },
  { value: "GREEK", label: t("equationEditor:greek") },
  { value: "ENGLISH", label: t("equationEditor:english") },
  { value: "CHEMISTRY", label: t("equationEditor:chemistry") },
];

const existingScript = () => document.getElementById("mathlive-script");

const loadMathLiveScripts = callback => {
  const isScriptLoaded = existingScript();
  if (!isScriptLoaded) {
    const script = document.createElement("script");
    script.src = "/mathlive/mathlive-0.107.0.min.js";
    script.id = "mathlive-script";
    script.defer = 1;
    document.head.appendChild(script);
    /* 
      Required to make mathlive work with monaco-editor. 
      monaco-editor uses custom AMD loader which conflicts with 
      mathlive script and breaks equation editor.
    */
    const originalDefine = window.define; // define from monaco-editor if loaded previously
    window.define = undefined; // Temporarily disable AMD loader
    script.onload = () => {
      window.define = originalDefine; // Restore AMD loader
      if (callback) callback();
    };
  }
  if (isScriptLoaded && callback) callback();
};

const MathEquationEditor = ({
  latex,
  latexList,
  closeEditor,
  onInsertButtonClick,
  setToastMsg,
  uploadFileV2,
  t,
  selectedText,
  testId,
  handleUpdateImage,
  allowCopyPasteInEquationEditor = true,
  disabledCopyButtonTooltipText = null,
}) => {
  const tabs = useRef(getTabs(t));
  const [_latex, _setLatex] = useState(selectedText ?? latex ?? "");
  const [copiedLatex, setCopiedLatex] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [currentTab, setCurrentTab] = useState(_.first(tabs.current)?.value);
  const [shiftOn, setShiftOn] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const mathLiveFieldContainerRef = useRef(null);

  const [showTooltip, setShowTooltip] = useState("");
  const [isMathliveScriptLoaded, setIsMathliveScriptLoaded] = useState(
    existingScript() ? true : false
  );
  const [isEditorCompatible, setIsEditorCompatible] = useState(true);
  const [isCopySupported, setIsCopySupported] = useState(false);
  const [showLatex, setShowLatex] = useState(false);

  const draggableRef = useRef(null);
  const { position, handleMouseDown, handleTouchStart } = useDrag({
    ref: draggableRef,
    restrictWithinViewport: false,
  });

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isUpdateAborted, setIsUpdateAborted] = useState(false);
  const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);
  const isOperationAbortedRef = useRef(false);

  let timer;

  useEffect(() => {
    // To update value of editor when user double clicks on equation image and editor is already open
    if (latex?.length > 0) {
      const mfe = document.getElementById(MATHFIELD_ID);
      mfe?.setValue(latex);
      _setLatex(latex);
    }
  }, [latex]);

  useEffect(() => {
    const deviceInfo = getBasicDeviceInfo();
    const versionArray = deviceInfo?.browser?.version?.split(".");
    const name = deviceInfo?.browser?.name;

    let shouldLoadScripts = true;

    if (name.toLowerCase() === "chrome") {
      setIsCopySupported(true);
    }

    if (versionArray.length >= 2 && name) {
      const major = parseInt(versionArray[0]);
      const minor = parseInt(versionArray[1]);

      switch (name.toLowerCase()) {
        case "chrome":
          if (major < 71) {
            shouldLoadScripts = false;
          }
          break;

        case "edge":
          if (major < 79) {
            shouldLoadScripts = false;
          }
          break;

        case "safari":
          if (major <= 12 && minor < 1) {
            shouldLoadScripts = false;
          }
          break;

        case "firefox [reality]":
        case "firefox":
          if (major < 65) {
            shouldLoadScripts = false;
          }
          break;

        case "opera":
        case "opera coast":
          if (major < 58) {
            shouldLoadScripts = false;
          }
          break;

        case "android browser":
          if (major < 71) {
            shouldLoadScripts = false;
          }
          break;

        case "samsung browser":
        case "samsung internet":
          if (major < 10) {
            shouldLoadScripts = false;
          }
          break;

        default:
          break;
      }
    }

    if (shouldLoadScripts) {
      loadMathLiveScripts(() => {
        setIsMathliveScriptLoaded(true);
      });
    } else {
      setIsEditorCompatible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keyup", testCapsLock);
    document.addEventListener("keydown", testCapsLock);

    function testCapsLock(event) {
      if (event.code === "CapsLock") {
        const isCapsLockOn = event.getModifierState("CapsLock");
        if (isCapsLockOn && !shiftOn) {
          setShiftOn(true);
          setCapsLockOn(true);
        } else {
          setShiftOn(false);
          setCapsLockOn(false);
        }
      }
    }

    return () => {
      document.removeEventListener("keyup", testCapsLock);
      document.removeEventListener("keydown", testCapsLock);
    };
  }, []);

  useEffect(() => {
    if (isMathliveScriptLoaded) {
      if (selectedText && mathLiveFieldContainerRef.current) {
        handleFormulaParseAndInsert(selectedText);
      }

      if (latexList?.length > 0 && mathLiveFieldContainerRef.current) {
        setShowUpdateModal(true);
        isOperationAbortedRef.current = false;
        handleFormulaParseAndInsert(latexList[0].latex, "insert", 0);
      }
    }
  }, [isMathliveScriptLoaded]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && showUpdateModal && !isUpdateAborted) {
        isOperationAbortedRef.current = true;
        setProcessing(false);
        setIsUpdateAborted(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [showUpdateModal, isUpdateAborted]);

  const toggleShiftState = () => {
    if (!capsLockOn) setShiftOn(shiftOn => !shiftOn);
    else
      setToastMsg({
        msg: "toastMsgs:capsLock_is_already_on",
        type: "warning",
        position: "toast-bottom-left",
      });
  };

  // onKeyboardButtonClick (virtual keyboard)
  const onKeyboardButtonClick = ({ latex, shiftLatex, onClick }) => {
    const mfe = mathLiveFieldContainerRef.current;
    const targetNode = mfe.shadowRoot.querySelectorAll(
      SHADOW_ROOT_QUERY_SELECTOR
    )[0];

    if (mfe && targetNode) {
      const { width } = targetNode.getBoundingClientRect();

      if (onClick) {
        onClick(mfe, toggleShiftState);
      } else if (width < MAX_EQUATION_WIDTH) {
        mfe.executeCommand([
          "insert",
          shiftOn && shiftLatex ? shiftLatex : latex,
          { insertionMode: "replaceSelection", selectionMode: "placeholder" },
        ]);
      } else {
        clearTimeout(timer);
        timer = setTimeout(
          () => setToastMsg("toastMsgs:equation_width_limit_reached"),
          500
        );
      }
      mfe.focus();
    }
  };

  const buttonsArr = useMemo(() => {
    const currentNode = MathEquationMenu["nodes"].find(node => {
      return node.id === currentTab;
    });
    const newButtonsArr = currentNode.children.map(nodeChild => {
      return (
        MathEquationMenu["nodeChildren"].find(
          buttonObj => buttonObj.id === nodeChild
        ) ||
        MathEquationMenu["commonChildren"].find(
          buttonObj => buttonObj.id === nodeChild
        )
      );
    });

    if (currentNode.id === "CHEMISTRY") {
      return newButtonsArr.map(btn => ({
        ...btn,
        variant: btn.id === "blank" ? btn.variant : "plain-subtle",
      }));
    }
    return newButtonsArr;
  }, [currentTab]);

  const rightButtonsArr = useMemo(() => {
    if (currentTab === "CHEMISTRY") {
      const newButtonsArr = MathEquationMenu["chemistry_right_nodes"].map(
        nodeChild => {
          return (
            MathEquationMenu["nodeChildren"].find(
              buttonObj => buttonObj.id === nodeChild
            ) ||
            MathEquationMenu["commonChildren"].find(
              buttonObj => buttonObj.id === nodeChild
            )
          );
        }
      );
      return newButtonsArr.map((btn, idx) =>
        btn.id === "insert"
          ? btn
          : {
              ...btn,
              variant:
                (idx + 1) % 5 === 0 || btn.id === "blank"
                  ? "plain-default"
                  : "plain-subtle",
            }
      );
    }
    return _.get(MathEquationMenu, "commonChildren", []);
  }, [currentTab]);

  // handleCopyImage
  const handleCopyImage = ({ height, width, latex, verticalAlign, src }) => {
    const image = new window.Image();
    image.src = src;
    image.height = height;
    image.width = width;
    image.style.verticalAlign = verticalAlign + "px";
    image.style.display = "inline";
    image.alt = latex.toString();
    image.className = "equation";
    image.dataset.latex = latex.toString();

    setCopiedLatex(latex);

    // Checking for browser compatibility of "Clipboard API"
    if (navigator.userAgent.indexOf("Firefox") != -1) {
      // it's Firefox and ClipboardItem isn't supported (by-default)
      // creating dummy element
      const clipboardDiv = document.createElement("div");
      clipboardDiv.style.fontSize = "12pt";
      clipboardDiv.style.border = "0";
      clipboardDiv.style.padding = "0";
      clipboardDiv.style.margin = "0";
      // hiding element
      clipboardDiv.style.position = "fixed";
      clipboardDiv.style["right"] = "-9999px";
      clipboardDiv.style.top =
        (window.pageYOffset || document.documentElement.scrollTop) + "px";
      clipboardDiv.setAttribute("readonly", "");
      clipboardDiv.style.opacity = 0;
      clipboardDiv.style.pointerEvents = "none";
      clipboardDiv.style.zIndex = -1000;
      clipboardDiv.setAttribute("tabindex", -1); // so it can be focused

      clipboardDiv.innerHTML = image.outerHTML;

      document.body.appendChild(clipboardDiv);

      clipboardDiv.focus();

      window.getSelection().removeAllRanges();
      var range = document.createRange();
      range.setStartBefore(clipboardDiv.firstChild);
      range.setEndAfter(clipboardDiv.lastChild);
      window.getSelection().addRange(range);

      try {
        if (document.execCommand("copy")) {
          setToastMsg({
            msg: "toastMsgs:equation_copied_successfully",
            type: "success",
            position: "toast-bottom-left",
          });
        } else {
          console.error("Copy failed");
        }
      } catch (err) {
        console.error("Copy failed with exception: ", err);
      }
      // Important to removeChild
      document.body.removeChild(clipboardDiv);
    } else {
      const type = "text/html";
      const blob = new Blob([image.outerHTML], { type });
      const data = new window.ClipboardItem({ [type]: blob });

      navigator.clipboard
        .write([data])
        .then(() => {
          setToastMsg({
            msg: "toastMsgs:equation_copied_successfully",
            type: "success",
            position: "toast-bottom-left",
          });
        })
        .catch(err => {
          console.error("Error in copying image: ", err);
        });
    }
  };

  const handleFormulaParseAndInsert = (latex, mode = "insert", idx = -1) => {
    // Check if operation was aborted before processing
    if (isOperationAbortedRef.current) {
      return;
    }

    if (mathLiveFieldContainerRef.current) {
      setProcessing(true);
      const imageProps = {};

      const mfe = mathLiveFieldContainerRef.current;
      const targetNode = mfe?.shadowRoot?.querySelectorAll(
        SHADOW_ROOT_QUERY_SELECTOR
      )[0];

      if (latexList?.length > 0 && latex) mfe?.setValue(latex);

      // Blur the editable text field first to remove cursor
      mfe?.blur();

      setTimeout(() => {
        // Check again after timeout if operation was aborted
        if (isOperationAbortedRef.current) {
          return;
        }

        const { height: h, width, bottom } = targetNode.getBoundingClientRect();
        const height = h + 2; // increasing due to bottom was getting cut off

        let verticalAlign = 0;
        const strutEle = targetNode
          ?.querySelector(".ML__strut")
          ?.getBoundingClientRect();

        if (strutEle) verticalAlign = -(bottom - strutEle.bottom);

        imageProps.verticalAlign = verticalAlign.toFixed(2);
        imageProps.height = height.toFixed(2);
        imageProps.width = width.toFixed(2);
        imageProps.latex = latex;

        convertMathToBlob(targetNode, {
          height,
          width,
          backgroundColor: null,
          logging: false,
          pixelRatio: 5,
        })
          .then(async blob => {
            // Check if operation was aborted during processing
            if (isOperationAbortedRef.current) {
              return;
            }

            if (blob) {
              let imageSrc;

              // Upload to server only if mode is "copy"
              if (mode === "copy") {
                const id = generateRandomId();
                const url = await uploadFileV2({
                  file: new File([blob], "id", {
                    type: "image/png",
                  }),
                  status: "COPYING",
                  attachment: {
                    type: "EQ",
                    mimeType: "image/png",
                    metadata: {
                      fileExtension: "png",
                      fileGroup: "content/eq",
                    },
                  },
                  uploadId: id,
                });
                imageSrc = url;
              } else {
                // For insert mode, use local blob URL
                imageSrc = URL.createObjectURL(blob);
              }

              imageProps.src = imageSrc;

              if (latexList?.length > 0 && latex) {
                const { height, width, verticalAlign } = imageProps;
                // Check again before upload
                if (isOperationAbortedRef.current) {
                  return;
                }

                const id = generateRandomId();
                const url = await uploadFileV2({
                  file: new File([blob], "id", {
                    type: "image/png",
                  }),
                  status: "INSERTING",
                  attachment: {
                    type: "EQ",
                    mimeType: "image/png",
                    metadata: {
                      fileExtension: "png",
                      fileGroup: "content/eq",
                    },
                  },
                  uploadId: id,
                });
                imageSrc = url;
                const imgEle = latexList[idx]?.ele;
                handleUpdateImage({
                  oldUrl: imgEle.src,
                  newUrl: imageSrc,
                  height,
                  width,
                  verticalAlign,
                });
              } else {
                if (mode === "insert") {
                  onInsertButtonClick(imageProps);
                } else {
                  handleCopyImage(imageProps);
                }
              }
            }
          })
          .catch(err => console.error(err))
          .finally(() => {
            // Don't proceed if operation was aborted
            if (isOperationAbortedRef.current) {
              return;
            }

            if (latexList?.length > 0) {
              if (idx + 1 < latexList.length) {
                handleFormulaParseAndInsert(
                  latexList[idx + 1].latex,
                  "insert",
                  idx + 1
                );
              } else {
                setProcessing(false);
                setIsUpdateSuccess(true);
              }
            } else {
              setProcessing(false);
              if (mode !== "copy") {
                closeEditor();
              } else {
                const mfe = document.getElementById(MATHFIELD_ID);
                mfe?.focus();
              }
            }
          });
      }, 150);
    }
  };
  const renderButtons = (buttonObj, ind) => {
    const Icon =
      shiftOn && buttonObj.shiftIcon ? buttonObj.shiftIcon : buttonObj.icon;

    let timer;

    //Time of the long press
    const TEMPO = 400;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    const mouseDown = () => {
      timer = setTimeout(function () {
        setShowTooltip(buttonObj.id);
      }, TEMPO);
    };

    const mouseUp = () => {
      setShowTooltip("");
      clearTimeout(timer);
    };

    if (
      (!isMobile && buttonObj.text) ||
      (buttonObj.text && showTooltip == buttonObj.id)
    ) {
      return (
        <Tooltip
          tooltip={buttonObj.text ?? buttonObj.id}
          key={buttonObj.id === "blank" ? `blank_${ind}` : buttonObj.id}
          visible={showTooltip == buttonObj.id}
          overlayInnerStyle={{
            padding: "6px 10px",
          }}
          align={{
            offset: [0, -5],
          }}
        >
          <Button
            id={buttonObj.id}
            size={"large"}
            variant="neutral"
            type="plain"
            style={buttonObj.style ?? {}}
            className={
              buttonObj.variant === "plain-subtle"
                ? classes.btnPlainSubtle
                : classes.btnPlainDefault
            }
            onClick={() => onKeyboardButtonClick(buttonObj)}
            isFullWidth
            disabled={buttonObj.id === "blank"}
            onTouchEnd={mouseUp}
            onTouchStart={mouseDown}
            testId={`${testId}:${buttonObj.id}`}
          >
            {buttonObj.id === "blank" ? <div /> : Icon ? <Icon /> : <div />}
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <Button
          id={buttonObj.id}
          size={"large"}
          variant="neutral"
          type="plain"
          style={buttonObj.style ?? {}}
          className={
            buttonObj.variant === "plain-subtle"
              ? classes.btnPlainSubtle
              : classes.btnPlainDefault
          }
          onClick={() => onKeyboardButtonClick(buttonObj)}
          isFullWidth
          disabled={buttonObj.id === "blank"}
          onTouchEnd={mouseUp}
          onTouchStart={mouseDown}
          testId={`${testId}:${buttonObj.id}`}
        >
          {buttonObj.id === "blank" ? <div /> : Icon ? <Icon /> : <div />}
        </Button>
      );
    }
  };

  return (
    <DSProvider dsVersion="2.0">
      {isEditorCompatible ? (
        createPortal(
          <div
            className={
              selectedText || latexList?.length > 0
                ? classes.hide
                : classes.draggable
            }
            ref={draggableRef}
            style={{
              top: position.y,
              left: position.x,
            }}
          >
            {isMathliveScriptLoaded ? (
              selectedText || latexList?.length > 0 ? (
                <MathLive
                  value={_latex}
                  onChange={e => _setLatex(e.target.value)}
                  shiftOn={shiftOn}
                  mathLiveFieldContainerRef={mathLiveFieldContainerRef}
                  timer={timer}
                  copiedLatex={copiedLatex}
                  setCopiedLatex={setCopiedLatex}
                  MATHFIELD_ID={MATHFIELD_ID}
                  SHADOW_ROOT_QUERY_SELECTOR={SHADOW_ROOT_QUERY_SELECTOR}
                  MAX_EQUATION_WIDTH={MAX_EQUATION_WIDTH}
                  automate={true}
                  allowCopyPasteInEquationEditor={
                    allowCopyPasteInEquationEditor
                  }
                />
              ) : (
                <div className={classes.mathEquationEditorContainer}>
                  <div
                    className={classes.header}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    role="button"
                    tabIndex={-1}
                    aria-label={t("equationEditor:equation_editor_heading")}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <DotsSixVerticalOutlined size="xxx-small" />
                      <h6>{t("equationEditor:equation_editor_heading")}</h6>
                    </div>
                    <IconButton
                      className={classes.closeButton}
                      icon={<CloseOutlined variant="subtle" />}
                      size="small"
                      variant="neutral"
                      type="plain"
                      onClick={() => closeEditor({ latex: _latex })}
                      testId={`${testId}:math-equation-editor:header`}
                    />
                  </div>

                  <div className={classes.body}>
                    <div style={{ display: "flex", marginBottom: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <MathLive
                            value={_latex}
                            onChange={e => _setLatex(e.target.value)}
                            shiftOn={shiftOn}
                            mathLiveFieldContainerRef={
                              mathLiveFieldContainerRef
                            }
                            timer={timer}
                            copiedLatex={copiedLatex}
                            setCopiedLatex={setCopiedLatex}
                            MATHFIELD_ID={MATHFIELD_ID}
                            SHADOW_ROOT_QUERY_SELECTOR={
                              SHADOW_ROOT_QUERY_SELECTOR
                            }
                            MAX_EQUATION_WIDTH={MAX_EQUATION_WIDTH}
                            allowCopyPasteInEquationEditor={
                              allowCopyPasteInEquationEditor
                            }
                          />
                          {ACLStore.can("FeatureFlag:ShowMathEqLatexCode") && (
                            <IconButton
                              icon={<CodeOutlined />}
                              variant="neutral"
                              type="plain"
                              onClick={() => {
                                setShowLatex(!showLatex);
                              }}
                              style={{
                                position: "absolute",
                                bottom: "8px",
                                right: "8px",
                              }}
                              testId={`${testId}:math-equation-editor:show-latex`}
                            />
                          )}
                        </div>
                        {showLatex && (
                          <TextAreaInput
                            style={{ marginTop: "10px" }}
                            minRows={1}
                            maxRows={2}
                            value={_latex}
                            placeholder={"Latex here..."}
                            onChange={e => {
                              const mfe = document.getElementById(MATHFIELD_ID);
                              mfe.setValue(e.target.value);
                            }}
                            testId={`${testId}:math-equation-editor:latex`}
                          />
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginLeft: "8px",
                        }}
                      >
                        <ConditionalWrapper
                          condition={
                            !isCopySupported || !allowCopyPasteInEquationEditor
                          }
                          wrapper={
                            <Tooltip
                              tooltip={
                                disabledCopyButtonTooltipText ||
                                t("equationEditor:copy_not_supported")
                              }
                            />
                          }
                          type={"wrapperAsElement"}
                        >
                          <IconButton
                            icon={<CopyOutlined variant="subtle" />}
                            variant="neutral"
                            type="outlined"
                            onClick={() => {
                              handleFormulaParseAndInsert(_latex, "copy");
                            }}
                            disabled={
                              !isCopySupported ||
                              processing ||
                              _latex === "" ||
                              !allowCopyPasteInEquationEditor
                            }
                            testId={`${testId}:math-equation-editor:latex-copy`}
                          />
                        </ConditionalWrapper>
                        <IconButton
                          icon={<DeleteOutlined variant="subtle" />}
                          variant="neutral"
                          type="outlined"
                          style={{ height: "100%" }}
                          onClick={() => {
                            const mfe = document.getElementById(MATHFIELD_ID);
                            mfe && mfe.executeCommand("deleteAll");
                            mfe.focus();
                          }}
                          disabled={processing || _latex === ""}
                          testId={`${testId}:math-equation-editor:delete-all`}
                        />
                      </div>
                    </div>
                    <div className={classes.buttonsContainer}>
                      <div className={classes.tabsContainer}>
                        <Tabs
                          options={tabs.current}
                          value={currentTab}
                          onChange={newTabValue => {
                            setCurrentTab(newTabValue);
                            const mfe = document.getElementById(MATHFIELD_ID);
                            mfe.focus();
                          }}
                          type="flexible"
                          theme="light"
                          testId={`${testId}:math-equation-editor`}
                        />
                      </div>
                      <div className={classes.innerButtonsContainer}>
                        <div className={classes.leftInnerButtonsContainer}>
                          {buttonsArr.map((buttonObj, ind) =>
                            renderButtons(buttonObj, ind)
                          )}
                        </div>
                        <div className={classes.rightInnerButtonsContainer}>
                          {rightButtonsArr.map((buttonObj, ind) => {
                            return buttonObj.id === "insert" ? (
                              <Button
                                size={"large"}
                                key={buttonObj.id}
                                variant={buttonObj.variant}
                                className={classes.insertButton}
                                style={buttonObj.style ?? {}}
                                onClick={() =>
                                  handleFormulaParseAndInsert(_latex)
                                }
                                isFullWidth={true}
                                disabled={processing || _latex === ""}
                                testId={`${testId}:${buttonObj.id}`}
                              >
                                {t(`equationEditor:${buttonObj.text}`)}
                              </Button>
                            ) : (
                              renderButtons(buttonObj, ind)
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              !(selectedText || latexList?.length > 0) && (
                <Loading containerStyle={{ height: "100%" }} />
              )
            )}
            {processing && !showUpdateModal ? <FullScreenLoader /> : null}
            {showUpdateModal ? (
              <UpdateEquationsModal
                testId={testId}
                isAborted={isUpdateAborted}
                isSuccess={isUpdateSuccess}
                onClose={() => {
                  setShowUpdateModal(false);
                  setIsUpdateAborted(false);
                  setIsUpdateSuccess(false);
                  closeEditor();
                }}
              />
            ) : null}
          </div>,
          document.body
        )
      ) : (
        <EmptyState
          illustration={WarningIllustration}
          primaryButton={
            <Button onClick={closeEditor} testId={`${testId}:primary`}>
              {t("common:close")}
            </Button>
          }
          size="large"
          title={t("equationEditor:equation_editor_not_compatible")}
          titleStyle={{}}
          testId={testId}
        />
      )}
    </DSProvider>
  );
};

const mapActionCreators = {
  setToastMsg,
  uploadFileV2,
};

export default compose(
  I18nHOC({ resource: ["common", "equationEditor"] }),
  connect(s => s, mapActionCreators, null)
)(MathEquationEditor);
